import {
  ActionIcon,
  Button,
  Card,
  Divider,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconBolt,
  IconClick,
  IconLayoutGrid,
  IconPhoto,
  IconPlus,
  IconReceipt,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import type { PreviewPayload } from '../inbox/template-preview';

/**
 * Messenger template builders, extracted from the old single-page modal so the
 * WhatsApp-style `new`/`edit` routes can share them. Each builder owns its field
 * state + validation + submit button (calls `onSave` with the API payload) and
 * streams a live `onChange(preview)` for the right-side preview pane. Pass
 * `initial` to prefill for editing. Mirrors the Instagram builders.
 */

export type TemplateKind = 'quick_replies' | 'generic' | 'button' | 'media' | 'receipt';

export const KIND_META: Record<
  TemplateKind,
  { label: string; color: string; Icon: typeof IconBolt; hint: string }
> = {
  quick_replies: {
    label: 'Quick replies',
    color: 'blue',
    Icon: IconBolt,
    hint: 'A message with up to 13 tappable reply chips.',
  },
  generic: {
    label: 'Carousel',
    color: 'indigo',
    Icon: IconLayoutGrid,
    hint: 'Up to 10 swipeable cards, each with an image, text and buttons.',
  },
  button: {
    label: 'Buttons',
    color: 'cyan',
    Icon: IconClick,
    hint: 'A text message with up to 3 buttons (link / postback / call).',
  },
  media: {
    label: 'Media',
    color: 'teal',
    Icon: IconPhoto,
    hint: 'An image or video by URL with an optional button.',
  },
  receipt: {
    label: 'Receipt',
    color: 'green',
    Icon: IconReceipt,
    hint: 'An order/transaction summary with line items and a total.',
  },
};

/** Strict button shape the Send API + zod DTO expect (discriminated union). */
export type Btn =
  | { type: 'web_url'; title: string; url: string }
  | { type: 'postback'; title: string; payload: string }
  | { type: 'phone_number'; title: string; payload: string }
  | { type: 'audio_call'; title: string };

/** Flat editor model — every field is ALWAYS present, so switching the button
 *  type never drops the value the user already typed; the strict union is only
 *  assembled at submit time (`toApiButton`). */
export interface EditorButton {
  type: 'web_url' | 'postback' | 'phone_number' | 'audio_call';
  title: string;
  url: string;
  payload: string;
}
export const emptyButton = (): EditorButton => ({
  type: 'web_url',
  title: '',
  url: '',
  payload: '',
});

export interface QuickReply {
  title: string;
  payload: string;
}

/** Editor-side carousel card (flat button model). */
export interface EditorCard {
  title: string;
  subtitle: string;
  imageUrl: string;
  buttons: EditorButton[];
}
export const emptyCard = (): EditorCard => ({ title: '', subtitle: '', imageUrl: '', buttons: [] });

/** API-side card (matches the FbGenericElement DTO). */
export interface ApiCard {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  buttons?: Btn[];
}

export function isHttpsUrl(v: string): boolean {
  try {
    return new URL(v.trim()).protocol === 'https:';
  } catch {
    return false;
  }
}

/** Per-button validation — inline messages keyed by the field they belong to
 *  (empty object = valid). audio_call has no value field (title only). */
export function buttonErrors(b: EditorButton): { title?: string; value?: string } {
  const errs: { title?: string; value?: string } = {};
  const title = b.title.trim();
  if (!title) errs.title = 'Required';
  else if (title.length > 20) errs.title = 'Max 20 characters';
  if (b.type === 'web_url') {
    if (!b.url.trim()) errs.value = 'Enter a link';
    else if (!isHttpsUrl(b.url)) errs.value = 'Must start with https://';
  } else if (b.type === 'postback') {
    if (!b.payload.trim()) errs.value = 'Enter a payload';
    else if (b.payload.length > 1000) errs.value = 'Max 1000 characters';
  } else if (b.type === 'phone_number') {
    if (!b.payload.trim()) errs.value = 'Enter a phone number';
    else if (!/^\+?[0-9\s\-()]{7,}$/.test(b.payload.trim())) errs.value = 'Enter a valid number';
  }
  return errs;
}

export function buttonsValid(buttons: EditorButton[]): boolean {
  return buttons.every((b) => {
    const e = buttonErrors(b);
    return !e.title && !e.value;
  });
}

/** Assemble the strict union the API expects, dropping the unused arm fields. */
export function toApiButton(b: EditorButton): Btn {
  const title = b.title.trim();
  if (b.type === 'web_url') return { type: 'web_url', title, url: b.url.trim() };
  if (b.type === 'phone_number') return { type: 'phone_number', title, payload: b.payload.trim() };
  if (b.type === 'audio_call') return { type: 'audio_call', title };
  return { type: 'postback', title, payload: b.payload.trim() };
}

/** API button → flat editor button, for prefilling the edit form. */
export function toEditorButton(b: Btn): EditorButton {
  return {
    type: b.type,
    title: b.title,
    url: b.type === 'web_url' ? b.url : '',
    payload: b.type === 'postback' || b.type === 'phone_number' ? b.payload : '',
  };
}

/** Repeater for Messenger buttons (link / postback / call / audio call). The
 *  flat editor model means switching the type never wipes the typed value.
 *  `max` caps the count (3 for generic/button, 1 for media). */
export function ButtonsEditor({
  buttons,
  onChange,
  showErrors,
  max = 3,
}: {
  buttons: EditorButton[];
  onChange: (b: EditorButton[]) => void;
  showErrors: boolean;
  max?: number;
}) {
  function set(i: number, next: EditorButton) {
    onChange(buttons.map((b, idx) => (idx === i ? next : b)));
  }
  return (
    <Stack gap="xs">
      {buttons.map((b, i) => {
        const errs = showErrors ? buttonErrors(b) : {};
        return (
          <Group key={i} wrap="nowrap" gap="xs" align="flex-start">
            <Select
              w={130}
              data={[
                { value: 'web_url', label: 'Link' },
                { value: 'postback', label: 'Postback' },
                { value: 'phone_number', label: 'Call' },
                { value: 'audio_call', label: 'Audio call' },
              ]}
              value={b.type}
              allowDeselect={false}
              onChange={(v) => set(i, { ...b, type: (v as EditorButton['type']) ?? b.type })}
            />
            <TextInput
              flex={1}
              placeholder="Button text"
              maxLength={20}
              value={b.title}
              error={errs.title}
              onChange={(e) => set(i, { ...b, title: e.currentTarget.value })}
            />
            {b.type !== 'audio_call' && (
              <TextInput
                flex={1}
                placeholder={
                  b.type === 'web_url'
                    ? 'https://…'
                    : b.type === 'phone_number'
                      ? '+15551234567'
                      : 'PAYLOAD'
                }
                value={b.type === 'web_url' ? b.url : b.payload}
                error={errs.value}
                onChange={(e) =>
                  set(
                    i,
                    b.type === 'web_url'
                      ? { ...b, url: e.currentTarget.value }
                      : { ...b, payload: e.currentTarget.value },
                  )
                }
              />
            )}
            <ActionIcon
              variant="subtle"
              color="red"
              mt={4}
              onClick={() => onChange(buttons.filter((_, idx) => idx !== i))}
              aria-label="Remove button"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        );
      })}
      {buttons.length < max && (
        <Button
          size="compact-xs"
          variant="subtle"
          leftSection={<IconPlus size={12} />}
          onClick={() => onChange([...buttons, emptyButton()])}
        >
          Add button
        </Button>
      )}
    </Stack>
  );
}

export function QuickReplyBuilder({
  onSave,
  onChange,
  sending,
  submitLabel = 'Save template',
  initial,
}: {
  onSave: (p: { text: string; quickReplies: QuickReply[] }) => void;
  onChange?: (p: PreviewPayload) => void;
  sending: boolean;
  submitLabel?: string;
  initial?: { text: string; quickReplies: QuickReply[] };
}) {
  const [text, setText] = useState(initial?.text ?? '');
  const [replies, setReplies] = useState<QuickReply[]>(
    initial?.quickReplies?.length ? initial.quickReplies : [{ title: '', payload: '' }],
  );
  const [attempted, setAttempted] = useState(false);
  useEffect(() => {
    onChange?.({ text, quickReplies: replies });
  }, [text, replies, onChange]);
  const valid =
    !!text.trim() && replies.length > 0 && replies.every((r) => r.title.trim() && r.payload.trim());
  function submit() {
    if (!valid) {
      setAttempted(true);
      notifications.show({ message: 'Please fix the highlighted fields', color: 'yellow' });
      return;
    }
    onSave({
      text: text.trim(),
      quickReplies: replies.map((r) => ({ title: r.title.trim(), payload: r.payload.trim() })),
    });
  }
  return (
    <Stack gap="sm">
      <Textarea
        label="Message"
        autosize
        minRows={2}
        value={text}
        error={attempted && !text.trim() ? 'Enter a message' : undefined}
        onChange={(e) => setText(e.currentTarget.value)}
      />
      <Text size="sm" fw={600}>
        Options ({replies.length}/13)
      </Text>
      {replies.map((r, i) => (
        <Group key={i} wrap="nowrap" gap="xs" align="flex-start">
          <TextInput
            flex={1}
            placeholder="Button label"
            maxLength={20}
            value={r.title}
            error={attempted && !r.title.trim() ? 'Required' : undefined}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setReplies((rs) => rs.map((x, idx) => (idx === i ? { ...x, title: value } : x)));
            }}
          />
          <TextInput
            flex={1}
            placeholder="PAYLOAD"
            value={r.payload}
            error={attempted && !r.payload.trim() ? 'Required' : undefined}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setReplies((rs) => rs.map((x, idx) => (idx === i ? { ...x, payload: value } : x)));
            }}
          />
          <ActionIcon
            variant="subtle"
            color="red"
            mt={4}
            disabled={replies.length === 1}
            onClick={() => setReplies((rs) => rs.filter((_, idx) => idx !== i))}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
      <Group justify="space-between">
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          disabled={replies.length >= 13}
          onClick={() => setReplies((rs) => [...rs, { title: '', payload: '' }])}
        >
          Add option
        </Button>
        <Button color="blue" loading={sending} onClick={submit}>
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

export function CarouselBuilder({
  onSave,
  onChange,
  sending,
  submitLabel = 'Save template',
  initial,
}: {
  onSave: (p: { elements: ApiCard[] }) => void;
  onChange?: (p: PreviewPayload) => void;
  sending: boolean;
  submitLabel?: string;
  initial?: { elements: ApiCard[] };
}) {
  const [cards, setCards] = useState<EditorCard[]>(
    initial?.elements?.length
      ? initial.elements.map((c) => ({
          title: c.title,
          subtitle: c.subtitle ?? '',
          imageUrl: c.imageUrl ?? '',
          buttons: (c.buttons ?? []).map(toEditorButton),
        }))
      : [emptyCard()],
  );
  const [attempted, setAttempted] = useState(false);
  useEffect(() => {
    onChange?.({
      elements: cards.map((c) => ({
        title: c.title,
        subtitle: c.subtitle,
        imageUrl: c.imageUrl,
        buttons: c.buttons.map((b) => ({ type: b.type, title: b.title, url: b.url })),
      })),
    });
  }, [cards, onChange]);
  const valid =
    cards.length > 0 &&
    cards.every(
      (c) =>
        c.title.trim() && (!c.imageUrl.trim() || isHttpsUrl(c.imageUrl)) && buttonsValid(c.buttons),
    );
  function upd(i: number, patch: Partial<EditorCard>) {
    setCards((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }
  function submit() {
    if (!valid) {
      setAttempted(true);
      notifications.show({ message: 'Please fix the highlighted fields', color: 'yellow' });
      return;
    }
    onSave({
      elements: cards.map((c) => ({
        title: c.title.trim(),
        ...(c.subtitle.trim() ? { subtitle: c.subtitle.trim() } : {}),
        ...(c.imageUrl.trim() ? { imageUrl: c.imageUrl.trim() } : {}),
        ...(c.buttons.length ? { buttons: c.buttons.map(toApiButton) } : {}),
      })),
    });
  }
  return (
    <Stack gap="md">
      {cards.map((card, i) => (
        <Card key={i} withBorder padding="sm">
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={600}>
              Card {i + 1}
            </Text>
            <ActionIcon
              variant="subtle"
              color="red"
              disabled={cards.length === 1}
              onClick={() => setCards((cs) => cs.filter((_, idx) => idx !== i))}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
          <Stack gap="xs">
            <TextInput
              label="Title"
              maxLength={80}
              value={card.title}
              error={attempted && !card.title.trim() ? 'Required' : undefined}
              onChange={(e) => upd(i, { title: e.currentTarget.value })}
            />
            <TextInput
              label="Subtitle"
              maxLength={80}
              value={card.subtitle}
              onChange={(e) => upd(i, { subtitle: e.currentTarget.value })}
            />
            <TextInput
              label="Image URL"
              placeholder="https://…/card.jpg"
              value={card.imageUrl}
              error={
                attempted && card.imageUrl.trim() && !isHttpsUrl(card.imageUrl)
                  ? 'Must start with https://'
                  : undefined
              }
              onChange={(e) => upd(i, { imageUrl: e.currentTarget.value })}
            />
            <ButtonsEditor
              buttons={card.buttons}
              showErrors={attempted}
              onChange={(b) => upd(i, { buttons: b })}
            />
          </Stack>
        </Card>
      ))}
      <Group justify="space-between">
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          disabled={cards.length >= 10}
          onClick={() => setCards((cs) => [...cs, emptyCard()])}
        >
          Add card
        </Button>
        <Button color="blue" loading={sending} onClick={submit}>
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

export function ButtonBuilder({
  onSave,
  onChange,
  sending,
  submitLabel = 'Save template',
  initial,
}: {
  onSave: (p: { text: string; buttons: Btn[] }) => void;
  onChange?: (p: PreviewPayload) => void;
  sending: boolean;
  submitLabel?: string;
  initial?: { text: string; buttons: Btn[] };
}) {
  const [text, setText] = useState(initial?.text ?? '');
  const [buttons, setButtons] = useState<EditorButton[]>(
    initial?.buttons?.length ? initial.buttons.map(toEditorButton) : [emptyButton()],
  );
  const [attempted, setAttempted] = useState(false);
  useEffect(() => {
    onChange?.({
      text,
      buttons: buttons.map((b) => ({ type: b.type, title: b.title, url: b.url })),
    });
  }, [text, buttons, onChange]);
  const valid = !!text.trim() && buttons.length > 0 && buttonsValid(buttons);
  function submit() {
    if (!valid) {
      setAttempted(true);
      notifications.show({ message: 'Please fix the highlighted fields', color: 'yellow' });
      return;
    }
    onSave({ text: text.trim(), buttons: buttons.map(toApiButton) });
  }
  return (
    <Stack gap="sm">
      <Textarea
        label="Message"
        autosize
        minRows={2}
        maxLength={640}
        value={text}
        error={attempted && !text.trim() ? 'Enter a message' : undefined}
        onChange={(e) => setText(e.currentTarget.value)}
      />
      <Text size="sm" fw={600}>
        Buttons ({buttons.length}/3)
      </Text>
      <ButtonsEditor buttons={buttons} showErrors={attempted} onChange={setButtons} />
      <Group justify="flex-end">
        <Button color="blue" loading={sending} onClick={submit}>
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

export function MediaBuilder({
  onSave,
  onChange,
  sending,
  submitLabel = 'Save template',
  initial,
}: {
  onSave: (p: { mediaType: 'image' | 'video'; url: string; buttons?: Btn[] }) => void;
  onChange?: (p: PreviewPayload) => void;
  sending: boolean;
  submitLabel?: string;
  initial?: { mediaType: 'image' | 'video'; url: string; buttons?: Btn[] };
}) {
  const [mediaType, setMediaType] = useState<'image' | 'video'>(initial?.mediaType ?? 'image');
  const [url, setUrl] = useState(initial?.url ?? '');
  // Meta caps the media template at ONE button — enforced here and server-side.
  const [buttons, setButtons] = useState<EditorButton[]>(
    initial?.buttons?.length ? initial.buttons.map(toEditorButton) : [],
  );
  const [attempted, setAttempted] = useState(false);
  useEffect(() => {
    onChange?.({
      mediaType,
      url,
      buttons: buttons.map((b) => ({ type: b.type, title: b.title, url: b.url })),
    });
  }, [mediaType, url, buttons, onChange]);
  const valid = isHttpsUrl(url) && buttonsValid(buttons);
  function submit() {
    if (!valid) {
      setAttempted(true);
      notifications.show({ message: 'Please fix the highlighted fields', color: 'yellow' });
      return;
    }
    onSave({
      mediaType,
      url: url.trim(),
      ...(buttons.length ? { buttons: buttons.map(toApiButton) } : {}),
    });
  }
  return (
    <Stack gap="sm">
      <Select
        label="Media type"
        data={[
          { value: 'image', label: 'Image' },
          { value: 'video', label: 'Video' },
        ]}
        value={mediaType}
        allowDeselect={false}
        onChange={(v) => setMediaType((v as 'image' | 'video') ?? 'image')}
      />
      <TextInput
        label="Media URL"
        description="Must be a public https:// URL Meta can fetch."
        placeholder="https://…/promo.jpg"
        value={url}
        error={attempted && !isHttpsUrl(url) ? 'Must start with https://' : undefined}
        onChange={(e) => setUrl(e.currentTarget.value)}
      />
      <Text size="sm" fw={600}>
        Button (optional, max 1)
      </Text>
      <ButtonsEditor buttons={buttons} showErrors={attempted} onChange={setButtons} max={1} />
      <Group justify="flex-end">
        <Button color="blue" loading={sending} onClick={submit}>
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

// ── Receipt ──────────────────────────────────────────────────────────────
// (ReceiptBuilder is defined below; BuilderSwitch at the bottom dispatches by kind.)

interface ReceiptItemEditor {
  title: string;
  subtitle: string;
  quantity: number | '';
  price: number | '';
}
export interface ReceiptPayload {
  recipientName: string;
  orderNumber: string;
  currency: string;
  paymentMethod: string;
  orderUrl?: string;
  summary: { subtotal?: number; shippingCost?: number; totalTax?: number; totalCost: number };
  elements: Array<{ title: string; subtitle?: string; quantity?: number; price: number }>;
}

export function ReceiptBuilder({
  onSave,
  onChange,
  sending,
  submitLabel = 'Save template',
  initial,
}: {
  onSave: (p: ReceiptPayload) => void;
  onChange?: (p: PreviewPayload) => void;
  sending: boolean;
  submitLabel?: string;
  initial?: ReceiptPayload;
}) {
  const [recipientName, setRecipientName] = useState(initial?.recipientName ?? '');
  const [orderNumber, setOrderNumber] = useState(initial?.orderNumber ?? '');
  const [currency, setCurrency] = useState(initial?.currency ?? 'USD');
  const [paymentMethod, setPaymentMethod] = useState(initial?.paymentMethod ?? '');
  const [orderUrl, setOrderUrl] = useState(initial?.orderUrl ?? '');
  const [items, setItems] = useState<ReceiptItemEditor[]>(
    initial?.elements?.length
      ? initial.elements.map((e) => ({
          title: e.title,
          subtitle: e.subtitle ?? '',
          quantity: e.quantity ?? '',
          price: e.price,
        }))
      : [{ title: '', subtitle: '', quantity: '', price: '' }],
  );
  const [subtotal, setSubtotal] = useState<number | ''>(initial?.summary?.subtotal ?? '');
  const [shippingCost, setShippingCost] = useState<number | ''>(
    initial?.summary?.shippingCost ?? '',
  );
  const [totalTax, setTotalTax] = useState<number | ''>(initial?.summary?.totalTax ?? '');
  const [totalCost, setTotalCost] = useState<number | ''>(initial?.summary?.totalCost ?? '');
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    onChange?.({
      receipt: {
        recipientName,
        orderNumber,
        currency,
        paymentMethod,
        summary: { totalCost: totalCost === '' ? undefined : totalCost },
        elements: items.map((it) => ({
          title: it.title,
          quantity: it.quantity === '' ? undefined : it.quantity,
          price: it.price === '' ? undefined : it.price,
        })),
      },
    });
  }, [recipientName, orderNumber, currency, paymentMethod, totalCost, items, onChange]);

  const itemsValid = items.length > 0 && items.every((it) => it.title.trim() && it.price !== '');
  const valid =
    !!recipientName.trim() &&
    !!orderNumber.trim() &&
    currency.trim().length === 3 &&
    !!paymentMethod.trim() &&
    totalCost !== '' &&
    itemsValid;

  function setItem(i: number, patch: Partial<ReceiptItemEditor>) {
    setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }
  function submit() {
    if (!valid) {
      setAttempted(true);
      notifications.show({ message: 'Please fix the highlighted fields', color: 'yellow' });
      return;
    }
    onSave({
      recipientName: recipientName.trim(),
      orderNumber: orderNumber.trim(),
      currency: currency.trim().toUpperCase(),
      paymentMethod: paymentMethod.trim(),
      ...(orderUrl.trim() ? { orderUrl: orderUrl.trim() } : {}),
      summary: {
        ...(subtotal !== '' ? { subtotal } : {}),
        ...(shippingCost !== '' ? { shippingCost } : {}),
        ...(totalTax !== '' ? { totalTax } : {}),
        totalCost: totalCost as number,
      },
      elements: items.map((it) => ({
        title: it.title.trim(),
        ...(it.subtitle.trim() ? { subtitle: it.subtitle.trim() } : {}),
        ...(it.quantity !== '' ? { quantity: it.quantity } : {}),
        price: it.price as number,
      })),
    });
  }

  return (
    <Stack gap="sm">
      <Group grow>
        <TextInput
          label="Recipient name"
          value={recipientName}
          error={attempted && !recipientName.trim() ? 'Required' : undefined}
          onChange={(e) => setRecipientName(e.currentTarget.value)}
        />
        <TextInput
          label="Order number"
          description="Must be unique per recipient"
          value={orderNumber}
          error={attempted && !orderNumber.trim() ? 'Required' : undefined}
          onChange={(e) => setOrderNumber(e.currentTarget.value)}
        />
      </Group>
      <Group grow>
        <TextInput
          label="Currency"
          description="ISO code, e.g. USD"
          maxLength={3}
          value={currency}
          error={attempted && currency.trim().length !== 3 ? '3-letter code' : undefined}
          onChange={(e) => setCurrency(e.currentTarget.value.toUpperCase())}
        />
        <TextInput
          label="Payment method"
          placeholder="Visa •••• 1234"
          value={paymentMethod}
          error={attempted && !paymentMethod.trim() ? 'Required' : undefined}
          onChange={(e) => setPaymentMethod(e.currentTarget.value)}
        />
      </Group>
      <TextInput
        label="Order URL (optional)"
        placeholder="https://…/orders/123"
        value={orderUrl}
        error={
          attempted && orderUrl.trim() && !isHttpsUrl(orderUrl) ? 'Must be https://' : undefined
        }
        onChange={(e) => setOrderUrl(e.currentTarget.value)}
      />

      <Divider label="Line items" labelPosition="left" />
      {items.map((it, i) => (
        <Card key={i} withBorder padding="sm">
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={600}>
              Item {i + 1}
            </Text>
            <ActionIcon
              variant="subtle"
              color="red"
              disabled={items.length === 1}
              onClick={() => setItems((xs) => xs.filter((_, idx) => idx !== i))}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
          <Stack gap="xs">
            <TextInput
              label="Title"
              maxLength={80}
              value={it.title}
              error={attempted && !it.title.trim() ? 'Required' : undefined}
              onChange={(e) => setItem(i, { title: e.currentTarget.value })}
            />
            <TextInput
              label="Subtitle"
              maxLength={80}
              value={it.subtitle}
              onChange={(e) => setItem(i, { subtitle: e.currentTarget.value })}
            />
            <Group grow>
              <NumberInput
                label="Quantity"
                min={1}
                value={it.quantity}
                onChange={(v) => setItem(i, { quantity: typeof v === 'number' ? v : '' })}
              />
              <NumberInput
                label="Price"
                decimalScale={2}
                value={it.price}
                error={attempted && it.price === '' ? 'Required' : undefined}
                onChange={(v) => setItem(i, { price: typeof v === 'number' ? v : '' })}
              />
            </Group>
          </Stack>
        </Card>
      ))}
      <Button
        size="xs"
        variant="light"
        leftSection={<IconPlus size={14} />}
        onClick={() =>
          setItems((xs) => [...xs, { title: '', subtitle: '', quantity: '', price: '' }])
        }
      >
        Add item
      </Button>

      <Divider label="Summary" labelPosition="left" />
      <Group grow>
        <NumberInput
          label="Subtotal"
          decimalScale={2}
          value={subtotal}
          onChange={(v) => setSubtotal(typeof v === 'number' ? v : '')}
        />
        <NumberInput
          label="Shipping"
          decimalScale={2}
          value={shippingCost}
          onChange={(v) => setShippingCost(typeof v === 'number' ? v : '')}
        />
      </Group>
      <Group grow>
        <NumberInput
          label="Tax"
          decimalScale={2}
          value={totalTax}
          onChange={(v) => setTotalTax(typeof v === 'number' ? v : '')}
        />
        <NumberInput
          label="Total cost"
          decimalScale={2}
          value={totalCost}
          error={attempted && totalCost === '' ? 'Required' : undefined}
          onChange={(v) => setTotalCost(typeof v === 'number' ? v : '')}
        />
      </Group>

      <Group justify="flex-end">
        <Button color="blue" loading={sending} onClick={submit}>
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

/** Dispatch to the right builder by kind. `initial` is the stored API payload
 *  (edit) or undefined (new); `onSave` receives the kind-specific API payload
 *  the create/update mutation expects. */
export function BuilderSwitch({
  kind,
  sending,
  submitLabel,
  initial,
  onChange,
  onSave,
}: {
  kind: TemplateKind;
  sending: boolean;
  submitLabel: string;
  initial?: unknown;
  onChange: (p: PreviewPayload) => void;
  onSave: (payload: unknown) => void;
}) {
  switch (kind) {
    case 'quick_replies':
      return (
        <QuickReplyBuilder
          sending={sending}
          submitLabel={submitLabel}
          onChange={onChange}
          onSave={onSave}
          initial={initial as { text: string; quickReplies: QuickReply[] } | undefined}
        />
      );
    case 'generic':
      return (
        <CarouselBuilder
          sending={sending}
          submitLabel={submitLabel}
          onChange={onChange}
          onSave={onSave}
          initial={initial as { elements: ApiCard[] } | undefined}
        />
      );
    case 'button':
      return (
        <ButtonBuilder
          sending={sending}
          submitLabel={submitLabel}
          onChange={onChange}
          onSave={onSave}
          initial={initial as { text: string; buttons: Btn[] } | undefined}
        />
      );
    case 'media':
      return (
        <MediaBuilder
          sending={sending}
          submitLabel={submitLabel}
          onChange={onChange}
          onSave={onSave}
          initial={
            initial as { mediaType: 'image' | 'video'; url: string; buttons?: Btn[] } | undefined
          }
        />
      );
    case 'receipt':
      return (
        <ReceiptBuilder
          sending={sending}
          submitLabel={submitLabel}
          onChange={onChange}
          onSave={onSave}
          initial={initial as ReceiptPayload | undefined}
        />
      );
  }
}
